import { useEffect, useRef } from 'react'
// Named import, not default: this package's CJS shape (__esModule flag + a separately
// re-assigned `exports.default`) makes Vite's esbuild dep pre-bundler resolve a default
// import to the whole exports namespace object instead of unwrapping to the Intercom
// function itself — `Intercom({...})` then throws "Intercom is not a function". The named
// export doesn't have that ambiguity.
import { Intercom } from '@intercom/messenger-js-sdk'
import { INTERCOM_APP_ID } from '@/lib/env'
import { useAuthStore } from '@/stores/auth'
import { useTeamStore } from '@/stores/team'

let booted = false

export function useIntercomBoot() {
  const profile = useAuthStore((s) => s.profile)
  const selectedTeamId = useAuthStore((s) => s.selectedTeamId)
  const team = useTeamStore((s) => s.team)
  const bootedRef = useRef(false)

  useEffect(() => {
    if (!INTERCOM_APP_ID || !profile?.id || bootedRef.current) return

    Intercom({
      app_id: INTERCOM_APP_ID,
      user_id: profile.id,
      name: [profile.firstName, profile.lastName].filter(Boolean).join(' '),
      email: profile.email ?? undefined,
      created_at: profile.createdAt ? Math.floor(new Date(profile.createdAt).getTime() / 1000) : undefined,
      company: selectedTeamId ? { company_id: selectedTeamId, name: team?.name ?? undefined } : undefined,
      // Custom attribute so Help Center articles can be targeted to admin-portal visitors only
      // (vs. the end-user-facing app) via Intercom's own audience rules — set once here at boot.
      platform: 'adminPortal',
    })

    booted = true
    bootedRef.current = true
  }, [profile, selectedTeamId, team])
}

export function shutdownIntercom() {
  if (!INTERCOM_APP_ID || !booted) return
  if (typeof window !== 'undefined' && window.Intercom) {
    ;(window.Intercom as (...args: unknown[]) => void)('shutdown')
    booted = false
  }
}

// Checks window.Intercom directly rather than the `booted` flag above — that flag lives in JS
// module state, which HMR can reset independently of whether the widget is actually booted in
// the browser (a stale-module-vs-live-DOM mismatch, same class of issue as shutdownIntercom
// could hit). window.Intercom itself is the real, HMR-proof signal that boot succeeded.
export function openIntercomMessenger() {
  if (!INTERCOM_APP_ID) return
  if (typeof window !== 'undefined' && window.Intercom) {
    ;(window.Intercom as (...args: unknown[]) => void)('showMessages')
  }
}

// Opens the Messenger's Help tab — Intercom's JS API has no documented command to jump straight
// into a specific Help Center collection (confirmed directly by Intercom staff; only the whole
// Help space or one specific article by numeric ID are addressable), so this is the closest
// real in-app launch to "open support for this page" without a manual click through Help search.
export function openIntercomHelp() {
  if (!INTERCOM_APP_ID) return
  if (typeof window !== 'undefined' && window.Intercom) {
    ;(window.Intercom as (...args: unknown[]) => void)('showSpace', 'help')
  }
}

// Opens one specific Help Center article by its numeric Intercom article ID — unlike collections,
// articles ARE directly addressable via the JS API, so pages with a clear single best-match
// article can deep-link straight to it instead of the general Help tab (see openIntercomHelp).
export function openIntercomArticle(articleId: string | number) {
  if (!INTERCOM_APP_ID) return
  if (typeof window !== 'undefined' && window.Intercom) {
    ;(window.Intercom as (...args: unknown[]) => void)('showArticle', articleId)
  }
}
